import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import {
  AHIcon,
  AHSeparator,
  AHText,
  AHTodoCategoryIcon,
  Box,
  TopTab,
  TopTabItem,
  useBoxStyle,
  useLayoutContext,
  useTheme,
  useTodoSorter,
} from "arkh";

import { FormattedMessage } from "react-intl";

import { ProfileStackScreenProps } from "./ProfileStack";
import { graphql, useLazyLoadQuery, useSubscription } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { ProfileScreenEmployee } from "./ProfileScreenEmployee";
import { useCurrentEmployeeSelectControllerState } from "../UserAdmin/useCurrentEmployeeSelectControllerState";
import { EmployeeSwitch } from "./EmployeeSwitch";
import { ProfileScreenQuery } from "./__generated__/ProfileScreenQuery.graphql";

import { keyExtractorId } from "@/src/common/keyExtractors";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useIsStoresprintAdmin } from "@/src/useHasFeatureFlag";

import { MoreItem } from "@/src/common/navigationHeaderOptions";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { TeamScheduleListItem } from "../Team/TeamHomeNavigator/TeamScheduleListItem/TeamScheduleListItem";
import { TodoCategorySectionHeader } from "../Team/TeamHomeNavigator/Team/TodoCategorySectionHeader";
import { useNowIndexes } from "../Team/TeamHomeNavigator/TeamSchedule/useNowIndexes";
import { DateTime } from "luxon";
import { useProfileScreenNavigationHeaderOptions } from "./useProfileScreenNavigationHeaderOptions";
import { getTeamScheduleListItemHeight } from "../Team/TeamHomeNavigator/TeamScheduleListItem/getTeamScheduleListItemHeight";
import Animated from "react-native-reanimated";
import { defaultLayoutTransition } from "@/src/defaultLayoutTransition";
import { TodoListItem } from "@/src/components/TodoListItem";

function InnerProfileScreen(props: {
  navigation: ProfileStackScreenProps<"ProfileMain">["navigation"];
  fetchKey: string;
}) {
  const theme = useTheme();
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [filterMode, setFilterMode] = useState<"ASSIGNED" | "CREATED">(
    "ASSIGNED"
  );
  const data = useLazyLoadQuery<ProfileScreenQuery>(
    graphql`
      query ProfileScreenQuery($currentEmployeeId: ID) {
        me {
          stores {
            id
          }
        }
        storeViewer(employeeId: $currentEmployeeId) {
          events {
            id
            startAt
            __typename
            ...TeamScheduleListItem_TeamScheduleEventOccasion
            ... on RoutineOccasion {
              routine {
                ownerTeam {
                  color
                }
                currentSchedule {
                  __typename
                }
                description
                descriptionImages {
                  __typename
                }
              }
              assigned {
                id
              }
              assignedRoles {
                id
              }
            }
            ... on TeamScheduleInlineRoutineOccasion {
              taskExecution {
                routineOccasion {
                  routine {
                    ownerTeam {
                      color
                    }
                    currentSchedule {
                      __typename
                    }
                    description
                    descriptionImages {
                      __typename
                    }
                  }
                  assigned {
                    id
                  }
                  assignedRoles {
                    id
                  }
                }
              }
            }
          }
        }
        store {
          id
          currentEmployee(id: $currentEmployeeId) {
            ...ProfileScreenEmployee_employee
            name
            id
          }
          myRole

          todoCategories(includeDefault: true) {
            id
            title
            icon
            assignedCount: todos(
              assignedToEmployeeId: $currentEmployeeId
              assignedToMe: true
              includeNewlyCompleted: false
              includeSubTodos: true
              status: PENDING
            ) {
              totalCount
            }

            createdCount: todos(
              createdByMe: true
              createdByEmployeeId: $currentEmployeeId
              includeNewlyCompleted: false
              includeSubTodos: true
              status: PENDING
            ) {
              totalCount
            }

            assignedTodos: todos(
              assignedToEmployeeId: $currentEmployeeId
              assignedToMe: true
              includeNewlyCompleted: true
              includeSubTodos: true
              status: PENDING
            ) {
              edges {
                node {
                  id
                  ordinal
                  ...TodoListItem_todo
                }
              }
            }

            createdTodos: todos(
              createdByMe: true
              createdByEmployeeId: $currentEmployeeId
              includeNewlyCompleted: true
              includeSubTodos: true
              status: PENDING
            ) {
              edges {
                node {
                  id
                  ordinal
                  ...TodoListItem_todo
                }
              }
            }
          }
        }
      }
    `,
    {
      currentEmployeeId: currentEmployeeId ?? null,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const intl_tokens = useIntlTokens();
  const isStoresprintAdmin = useIsStoresprintAdmin();
  const now = DateTime.now();
  const [nowIndexes] = useNowIndexes({
    now,
    events:
      data.storeViewer?.events.flatMap((e) => {
        if (
          e?.__typename === "RoutineOccasion" &&
          e.routine?.currentSchedule?.__typename === "RoutineScheduleDaily"
        ) {
          return [e];
        }
        if (
          e?.__typename === "TeamScheduleInlineRoutineOccasion" &&
          e.taskExecution?.routineOccasion?.routine?.currentSchedule
            ?.__typename === "RoutineScheduleDaily"
        ) {
          return [e];
        }
        return [];
      }) ?? [],
  });
  const extraMoreItem = useMemo(() => {
    if (isStoresprintAdmin || (data.me && data.me.stores.length > 1)) {
      return {
        icon: "store",
        label: (
          <FormattedMessage
            defaultMessage="Change Store"
            description="MyProfile:changeStore"
          />
        ),
        onPress: () => {
          props.navigation.navigateDeprecated("SetStore", {
            storeId: data.store.id,
          });
        },
      } as MoreItem;
    }
    return null;
  }, [isStoresprintAdmin, data.me, data.store.id, props.navigation]);
  const options = useProfileScreenNavigationHeaderOptions({
    moreItems: extraMoreItem ? [extraMoreItem] : [],
  });
  useLayoutEffect(() => {
    props.navigation.setOptions(options);
  }, [options, props.navigation]);

  const todoIds = useMemo(() => {
    return data.store.todoCategories.flatMap((category) => {
      const assignedIds = category.assignedTodos.edges.flatMap((todo) =>
        todo.node ? [todo.node.id] : []
      );
      const createdIds = category.createdTodos.edges.flatMap((todo) =>
        todo.node ? [todo.node.id] : []
      );
      return [...assignedIds, ...createdIds];
    });
  }, [data.store.todoCategories]);

  useSubscription(
    useMemo(
      () => ({
        subscription: graphql`
          subscription ProfileScreenTodoChangedByIdsSubscription(
            $todoIds: [ID!]!
          ) {
            todoChangedByIds(todoIds: $todoIds) {
              id
              ...TodoListItem_todo
            }
          }
        `,
        variables: {
          todoIds,
        },
      }),
      [todoIds]
    )
  );

  useSubscription(
    useMemo(
      () => ({
        variables: {
          employeeId: currentEmployeeId,
        },
        subscription: graphql`
          subscription ProfileScreenAssignedRoutineOccasionChangedSubscription(
            $employeeId: ID
          ) {
            storeViewerAssignedRoutineOccasionChanged(employeeId: $employeeId) {
              events {
                id
                startAt
                __typename
                ...TeamScheduleListItem_TeamScheduleEventOccasion
                ... on RoutineOccasion {
                  routine {
                    ownerTeam {
                      color
                    }
                    currentSchedule {
                      __typename
                    }
                    description
                    descriptionImages {
                      __typename
                    }
                  }
                  assigned {
                    id
                  }
                  assignedRoles {
                    id
                  }
                }
                ... on TeamScheduleInlineRoutineOccasion {
                  taskExecution {
                    routineOccasion {
                      routine {
                        ownerTeam {
                          color
                        }
                        currentSchedule {
                          __typename
                        }
                        description
                        descriptionImages {
                          __typename
                        }
                      }
                      assigned {
                        id
                      }
                      assignedRoles {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        `,
      }),
      [currentEmployeeId]
    )
  );

  const isGroupAccount = data?.store?.myRole === "COWORKER_GROUP";
  const layout = useLayoutContext();
  const { openModal } = useCurrentEmployeeSelectControllerState();
  const openTodo = useCallback(
    (fromResponse: boolean, todoId: string) => {
      props.navigation.navigate("TodoEdit", {
        todoId,
        autofocusResponse: fromResponse,
      });
    },
    [props.navigation]
  );

  const employee = data.store.currentEmployee;
  const ListHeader = useMemo(() => {
    if (!isGroupAccount && !employee) {
      return null;
    }
    return (
      <Box
        spaceBottom="medium"
        color="surface"
        spaceTop="small"
        style={{
          paddingTop: layout.space.small + 200,
          marginTop: -200,
        }}
      >
        {isGroupAccount ? (
          <Box spaceHorizontal="medium" spaceVertical="small">
            <EmployeeSwitch
              currentEmployeeName={data.store.currentEmployee?.name ?? "Guest"}
              onPress={() => {
                openModal();
              }}
            />
          </Box>
        ) : null}

        {employee ? (
          <ProfileScreenEmployee employee={employee} />
        ) : isGroupAccount ? (
          <Box spaceHorizontal="medium">
            <AHIcon
              type="guest-actor"
              forceSize={layout.space.xlarge}
              spaceVertical="small"
              color="text-secondary"
            />
            <AHText type="title-big" spaceVertical="small">
              {intl_tokens.Guest}
            </AHText>
          </Box>
        ) : null}
      </Box>
    );
  }, [
    isGroupAccount,
    employee,
    layout.space.small,
    layout.space.xlarge,
    data.store.currentEmployee?.name,
    intl_tokens.Guest,
    openModal,
  ]);

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const FilterTopTab = useMemo(() => {
    return (
      <TopTab isCompact={isCompact}>
        <TopTabItem
          selected={filterMode === "ASSIGNED"}
          onPress={() => setFilterMode("ASSIGNED")}
        >
          <FormattedMessage
            defaultMessage="Assigned"
            description="Profile:filterAssigned"
          />
        </TopTabItem>
        <TopTabItem
          selected={filterMode === "CREATED"}
          onPress={() => setFilterMode("CREATED")}
        >
          <FormattedMessage
            defaultMessage="Created"
            description="Profile:filterCreated"
          />
        </TopTabItem>
      </TopTab>
    );
  }, [filterMode, isCompact]);

  const todoSorter = useTodoSorter();

  const listData = useMemo(() => {
    if (isGroupAccount && !employee) {
      return [];
    }

    const teamScheduleEventOccasions =
      data.storeViewer?.events
        .flatMap((event, index) => {
          if (!event) {
            return [];
          }
          const scheduleTypeName =
            event.__typename === "RoutineOccasion"
              ? event.routine?.currentSchedule?.__typename
              : event.__typename === "TeamScheduleInlineRoutineOccasion"
                ? event.taskExecution?.routineOccasion?.routine?.currentSchedule
                    ?.__typename
                : null;

          const itemType =
            scheduleTypeName === "RoutineScheduleWeekly"
              ? ("WEEKLY" as const)
              : scheduleTypeName === "RoutineScheduleMonthly"
                ? ("MONTHLY" as const)
                : scheduleTypeName === "RoutineScheduleDaily"
                  ? ("timeOfDay" as const)
                  : null;

          const routineOccasion =
            event.__typename === "RoutineOccasion"
              ? event
              : event.__typename === "TeamScheduleInlineRoutineOccasion"
                ? event.taskExecution?.routineOccasion
                : null;

          if (!itemType || !routineOccasion) {
            return [];
          }

          return [
            {
              type: "teamScheduleEventOccasion" as const,
              itemType,
              teamColor: event.routine?.ownerTeam?.color ?? theme.primary,
              isFirst: index === 0,
              height: getTeamScheduleListItemHeight(
                itemType === "timeOfDay" ? "DAILY" : itemType,
                ((routineOccasion.assigned?.length ?? 0) > 0 ||
                  (routineOccasion.assignedRoles?.length ?? 0) > 0) &&
                  ((routineOccasion.routine?.description?.length ?? 0) > 0 ||
                    (routineOccasion.routine?.descriptionImages?.length ?? 0) >
                      0)
              ),
              ...event,
            },
          ];
        })
        .map((e, index, data) => ({
          ...e,
          isLast: index === data.length - 1,
        })) ?? [];

    return [
      ...(filterMode !== "CREATED" && teamScheduleEventOccasions.length > 0
        ? [
            {
              type: "teamScheduleEventOccasionHeader" as const,
              count: teamScheduleEventOccasions.length,
              id: "teamScheduleEventOccasionHeader",
            },
          ]
        : []),
      ...(filterMode !== "CREATED" ? teamScheduleEventOccasions : []),
      ...data.store.todoCategories
        .slice()
        .sort((a, b) => {
          // Sort by assignedCount (not by current filter's count) to maintain
          // stable ordering when switching between ASSIGNED and CREATED filters.
          // This prevents jarring UI reordering when the user changes filters.
          return (
            (b.assignedCount?.totalCount ?? 0) -
            (a.assignedCount?.totalCount ?? 0)
          );
        })
        .flatMap((category) => {
          const todos =
            filterMode === "CREATED"
              ? category.createdTodos
              : category.assignedTodos;

          return [
            {
              type: "category" as const,
              id: category.id,
              title: category.title,
              assignedCount:
                filterMode === "CREATED"
                  ? (category.createdCount?.totalCount ?? 0)
                  : (category.assignedCount?.totalCount ?? 0),
              isLast: todos.edges.length === 0,
              icon: category.icon,
            },
            ...todos.edges
              .slice()
              .sort((a, b) => {
                return todoSorter(a?.node, b?.node);
              })
              .map((todo, index) => {
                return {
                  type: "todo" as const,
                  isLast: index === todos.edges.length - 1,
                  ...todo.node,
                };
              }),
          ];
        }),
    ];
  }, [
    data.store.todoCategories,
    data.storeViewer?.events,
    employee,
    filterMode,
    isGroupAccount,
    theme.primary,
    todoSorter,
  ]);

  const dailyStartIndex = listData.findIndex(
    (e) => e.type === "teamScheduleEventOccasion" && e.itemType === "timeOfDay"
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      switch (item.type) {
        case "category":
          return (
            <TodoCategorySectionHeader
              spaceHorizontal="medium"
              icon={<AHTodoCategoryIcon icon={item.icon ?? "Check"} />}
              notResolvedCount={item.assignedCount}
              title={item.title}
              ctaPress={() => {
                props.navigation.navigateDeprecated(
                  "TodoCategoryAssignedToEmployee",
                  {
                    todoCategoryId: item.id,
                    filterMode: filterMode,
                  }
                );
              }}
              ctaTitle={intl_tokens.SeeMore}
              isLast={item.isLast}
            />
          );
        case "todo":
          return (
            <Box key={item.id} spaceHorizontal="medium">
              <TodoListItem
                openTodo={openTodo}
                todo={item}
                isLast={item.isLast}
              />
              {item.isLast ? null : <AHSeparator />}
            </Box>
          );
        case "teamScheduleEventOccasionHeader":
          return (
            <TodoCategorySectionHeader
              spaceHorizontal="medium"
              icon={<AHIcon type="routine" />}
              notResolvedCount={item.count}
              title={intl_tokens.Routines}
              isLast={item.count === 0}
            />
          );
        case "teamScheduleEventOccasion": {
          const status = () => {
            const dailyIndex = index - (dailyStartIndex ?? 0);
            if (item.itemType === "timeOfDay") {
              if (nowIndexes.includes(dailyIndex)) {
                return "active";
              }
              if (
                nowIndexes.includes(dailyIndex - 1) &&
                DateTime.fromISO(item.startAt) < now.plus({ hour: 1 })
              ) {
                return "soon";
              }
            }
            return "none";
          };
          return (
            <Box style={{ height: item.height }}>
              <TeamScheduleListItem
                eventOccasion={item}
                isFirst={false}
                isLast={item.isLast}
                itemType={item.itemType}
                status={status()}
                teamColor={item.teamColor}
                radius="large"
                border
                showSeparator={!item.isFirst}
              />
            </Box>
          );
        }
      }
    },
    [
      intl_tokens.SeeMore,
      intl_tokens.Routines,
      openTodo,
      props.navigation,
      dailyStartIndex,
      nowIndexes,
      now,
      filterMode,
    ]
  );

  const safeAreaInsets = useSafeAreaInsets();
  const contentContainerStyle = useBoxStyle({
    spaceBottom: "medium",
    insets: {
      bottom: isCompact ? safeAreaInsets.bottom : undefined,
    },
  });

  return (
    <Animated.FlatList
      contentContainerStyle={contentContainerStyle}
      ListHeaderComponent={
        <>
          {ListHeader}
          {isGroupAccount && !employee ? null : FilterTopTab}
        </>
      }
      data={listData}
      renderItem={renderItem}
      keyExtractor={keyExtractorId}
      itemLayoutAnimation={defaultLayoutTransition}
    />
  );
}

export function ProfileScreen(props: ProfileStackScreenProps<"ProfileMain">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerProfileScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
